import { z } from 'zod';
import { Id, IsoDateTime, Currency } from './common.js';
import { DayOfWeek, TimeOfDay } from './business.js';

export const Service = z.object({
  service_id: Id,
  merchant_id: Id,
  name: z.string(),
  description: z.string().default(''),
  duration_minutes: z.number().int().positive(),
  price_cents: z.number().int().nonnegative(),
  currency: Currency,
  requirements: z.string().default(''),
  prerequisites: z.array(Id).default([]),
  allowed_team_members: z.array(Id).default([]),
  image_url: z.string().nullable(),
  is_active: z.boolean().default(true),
});
export type Service = z.infer<typeof Service>;

export const TeamMember = z.object({
  team_member_id: Id,
  merchant_id: Id,
  user_id: Id.nullable(),
  name: z.string(),
  role: z.string().default(''),
  specialties: z.array(z.string()).default([]),
  bio: z.string().default(''),
  image_url: z.string().nullable(),
  is_active: z.boolean().default(true),
});

export const AvailabilityRule = z.object({
  rule_id: Id,
  merchant_id: Id,
  team_member_id: Id.nullable(),
  service_id: Id.nullable(),
  day_of_week: DayOfWeek,
  start_time: TimeOfDay,
  end_time: TimeOfDay,
  slot_duration_minutes: z.number().int().positive().default(30),
  buffer_minutes: z.number().int().nonnegative().default(0),
  max_bookings_per_day: z.number().int().positive().nullable(),
  lead_time_minutes: z.number().int().nonnegative().default(60),
  booking_window_days: z.number().int().positive().default(30),
});
export type AvailabilityRule = z.infer<typeof AvailabilityRule>;

export const AvailabilityOverride = z.object({
  override_id: Id,
  merchant_id: Id,
  team_member_id: Id.nullable(),
  date: z.string(),
  is_closed: z.boolean(),
  start_time: TimeOfDay.nullable(),
  end_time: TimeOfDay.nullable(),
  reason: z.string().default(''),
});

export const AppointmentStatus = z.enum([
  'pending',
  'confirmed',
  'cancelled',
  'completed',
  'no_show',
]);
export type AppointmentStatus = z.infer<typeof AppointmentStatus>;

export const Appointment = z.object({
  appointment_id: Id,
  merchant_id: Id,
  customer_id: Id,
  service_id: Id,
  team_member_id: Id.nullable(),
  start_at: IsoDateTime,
  end_at: IsoDateTime,
  status: AppointmentStatus,
  customer_notes: z.string().default(''),
  merchant_notes: z.string().default(''),
  created_via: z.enum(['ai', 'merchant_manual', 'customer_direct']),
  conversation_id: Id.nullable(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type Appointment = z.infer<typeof Appointment>;

export const AppointmentReminder = z.object({
  reminder_id: Id,
  appointment_id: Id,
  merchant_id: Id,
  reminder_at: IsoDateTime,
  channel_id: Id,
  status: z.enum(['scheduled', 'sent', 'failed', 'cancelled']),
  template_id: Id.nullable(),
  sent_at: IsoDateTime.nullable(),
  error: z.string().nullable(),
});
